import React from "react";

import "../../styles/hrPageShell.css";
function extractSharePointChoiceText(value) {
  if (value === undefined || value === null) return "";
  if (typeof value === "object") {
    if (Array.isArray(value)) return value.map(extractSharePointChoiceText).filter(Boolean).join("; ");
    return extractSharePointChoiceText(value.Value ?? value.value ?? value.Title ?? value.Email ?? value.DisplayName ?? value.Name ?? value.Label ?? "");
  }
  let text = String(value).trim();
  if (!text) return "";

  // Common case: actual object was converted to JSON string.
  if ((text.startsWith("{") && text.endsWith("}")) || (text.startsWith("[") && text.endsWith("]"))) {
    try {
      return extractSharePointChoiceText(JSON.parse(text));
    } catch {
      // Continue with regex extraction below.
    }
  }

  // Defensive case: string contains the JSON object inside a larger value, e.g.
  // {"@odata.type":"...SPListExpandedReference","Id":0,"Value":"INR"}
  const valueMatch = text.match(/"Value"\s*:\s*"([^"]+)"/i) || text.match(/'Value'\s*:\s*'([^']+)'/i);
  if (valueMatch?.[1]) return valueMatch[1];

  const emailMatch = text.match(/"Email"\s*:\s*"([^"]+)"/i);
  if (emailMatch?.[1]) return emailMatch[1];

  const titleMatch = text.match(/"Title"\s*:\s*"([^"]+)"/i);
  if (titleMatch?.[1]) return titleMatch[1];

  return text;
}

function displayText(value, fallback = "-") {
  const text = extractSharePointChoiceText(value);
  return text || fallback;
}

function toNumberClean(value) {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number") return value;
  const text = extractSharePointChoiceText(value);
  // Remove embedded SP choice/lookup JSON pieces if any and keep numeric part only.
  const numericCandidate = text.replace(/\{[^{}]*"Value"\s*:\s*"[^"]+"[^{}]*\}/g, "").replace(/[^0-9.-]/g, "");
  if (!numericCandidate) return null;
  const number = Number(numericCandidate);
  return Number.isNaN(number) ? null : number;
}

function formatMoney(value, currency = "") {
  if (value === undefined || value === null || value === "") return "-";
  const cleanCurrency = extractSharePointChoiceText(currency) || "INR";
  const number = toNumberClean(value);
  if (number === null) return displayText(value);
  try {
    return new Intl.NumberFormat("en-IN", {
      style: cleanCurrency ? "currency" : "decimal",
      currency: cleanCurrency || "INR",
      maximumFractionDigits: 0,
    }).format(number);
  } catch {
    return `${cleanCurrency ? `${cleanCurrency} ` : ""}${number.toLocaleString("en-IN")}`;
  }
}

function formatDate(value) {
  const cleanValue = extractSharePointChoiceText(value);
  if (!cleanValue) return "-";
  const date = new Date(cleanValue);
  if (Number.isNaN(date.getTime())) return cleanValue;
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function EmployeeCTCHistoryPanel({ employeeCode = "", history = [], open = false, onClose }) {
  if (!open) return null;

  return (
    <div style={overlayStyle}>
      <div style={panelStyle}>
        <div style={headerStyle}>
          <div>
            <h3 style={{ margin: 0, fontSize: "24px" }}>CTC History</h3>
            <p style={smallTextStyle}>Employee: {displayText(employeeCode)}</p>
          </div>
          <button type="button" style={closeButtonStyle} onClick={onClose}>Close</button>
        </div>

        {history.length === 0 ? (
          <div style={emptyStyle}>No CTC history found.</div>
        ) : (
          <div style={tableWrapperStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Changed On</th>
                  <th style={thStyle}>Change Type</th>
                  <th style={thStyle}>Employee CTC ID</th>
                  <th style={thStyle}>Structure</th>
                  <th style={thStyle}>Currency</th>
                  <th style={thStyle}>Previous Annual CTC</th>
                  <th style={thStyle}>New Annual CTC</th>
                  <th style={thStyle}>Previous Monthly Gross</th>
                  <th style={thStyle}>New Monthly Gross</th>
                  <th style={thStyle}>Revision</th>
                  <th style={thStyle}>Effective From</th>
                  <th style={thStyle}>Changed By</th>
                  <th style={thStyle}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {history.map((row, index) => {
                  const cleanCurrency = displayText(row.salaryCurrency, "INR");
                  return (
                    <tr key={row.historyId || row.employeeCTCHistoryId || row.employeeCTCId || index}>
                      <td style={tdStyle}>{formatDate(row.changedOn)}</td>
                      <td style={tdStyle}><span style={pillStyle}>{displayText(row.changeType)}</span></td>
                      <td style={tdStyle}>{displayText(row.employeeCTCId)}</td>
                      <td style={tdStyle}>{displayText(row.ctcStructureId)}</td>
                      <td style={tdStyle}>{cleanCurrency}</td>
                      <td style={tdStyle}>{formatMoney(row.previousAnnualCTC, cleanCurrency)}</td>
                      <td style={tdStyle}>{formatMoney(row.newAnnualCTC, cleanCurrency)}</td>
                      <td style={tdStyle}>{formatMoney(row.previousMonthlyGross, cleanCurrency)}</td>
                      <td style={tdStyle}>{formatMoney(row.newMonthlyGross, cleanCurrency)}</td>
                      <td style={tdStyle}>{displayText(row.revisionNumber)}</td>
                      <td style={tdStyle}>{formatDate(row.effectiveFrom)}</td>
                      <td style={tdStyle}>{displayText(row.changedBy)}</td>
                      <td style={reasonTdStyle}>{displayText(row.changeReason)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const overlayStyle = { position: "fixed", inset: 0, backgroundColor: "rgba(15, 23, 42, 0.45)", zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "center", padding: "20px" };
const panelStyle = { backgroundColor: "var(--fm-card)", borderRadius: "14px", padding: "20px", width: "min(1400px, 96vw)", maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 50px rgba(15, 23, 42, 0.35)" };
const headerStyle = { display: "flex", justifyContent: "space-between", gap: "16px", alignItems: "center", marginBottom: "16px" };
const smallTextStyle = { margin: "6px 0 0", color: "var(--fm-muted)" };
const closeButtonStyle = { backgroundColor: "var(--fm-card)", border: "1px solid var(--fm-border)", borderRadius: "8px", padding: "9px 14px", fontWeight: 700, cursor: "pointer" };
const emptyStyle = { border: "1px solid var(--fm-border)", backgroundColor: "#f8fafc", color: "var(--fm-muted)", borderRadius: "10px", padding: "18px", textAlign: "center" };
const tableWrapperStyle = { overflowX: "auto" };
const tableStyle = { width: "100%", borderCollapse: "collapse", minWidth: "1300px", fontSize: "13px" };
const thStyle = { textAlign: "left", borderBottom: "1px solid var(--fm-border)", padding: "10px", backgroundColor: "#f8fafc", whiteSpace: "nowrap" };
const tdStyle = { borderBottom: "1px solid var(--fm-border)", padding: "10px", whiteSpace: "nowrap", verticalAlign: "top" };
const reasonTdStyle = { ...tdStyle, whiteSpace: "normal", minWidth: "220px", maxWidth: "420px" };
const pillStyle = { display: "inline-block", background: "#e0f2fe", color: "var(--fm-page-accent)", borderRadius: "999px", padding: "3px 8px", fontSize: "11px", fontWeight: 800 };

export default EmployeeCTCHistoryPanel;
